import { useEffect, useRef } from "react";
import L from "leaflet";

/** Compass bearing from → to (0° = north, clockwise) — aims the panorama at the 2nd click. */
export function headingBetween(from: L.LatLng, to: L.LatLng): number {
  const dLon = ((to.lng - from.lng) * Math.PI) / 180;
  const lat1 = (from.lat * Math.PI) / 180;
  const lat2 = (to.lat * Math.PI) / 180;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x = Math.cos(lat1) * Math.sin(lat2) - Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

/** Google Street View panorama at `at`, looking `heading` degrees from north. */
export function streetViewUrl(at: L.LatLng, heading: number): string {
  return `https://maps.google.com/maps?layer=c&cbll=${at.lat},${at.lng}&cbp=11,${Math.round(heading)},0,0,0`;
}

/**
 * Street View click-tool — the same two-phase gesture as the power-project map: the first
 * click drops a red anchor, a dashed rubber-band follows the cursor, the second click sets
 * the direction, opens the panorama in a new tab and calls `onDone` (the caller disarms).
 * Deactivating mid-gesture clears the anchor. The caller makes its own vector layers
 * click-through while the tool is armed, so the click reaches the map.
 */
export function useStreetViewTool(map: L.Map | null, active: boolean, onDone: () => void): void {
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  useEffect(() => {
    if (!map || !active) return;
    const el = map.getContainer();
    el.style.cursor = "crosshair";
    let sv: { anchor: L.LatLng; marker: L.CircleMarker; line: L.Polyline | null } | null = null;

    const clear = () => {
      if (!sv) return;
      map.removeLayer(sv.marker);
      if (sv.line) map.removeLayer(sv.line);
      sv = null;
    };

    const onClick = (e: L.LeafletMouseEvent) => {
      if (!sv) {
        const marker = L.circleMarker(e.latlng, {
          radius: 8, color: "#fff", weight: 2, fillColor: "#ef4444", fillOpacity: 1,
          pmIgnore: true,
        } as L.CircleMarkerOptions).addTo(map);
        sv = { anchor: e.latlng, marker, line: null };
        return;
      }
      window.open(streetViewUrl(sv.anchor, headingBetween(sv.anchor, e.latlng)), "_blank", "noopener,noreferrer");
      clear();
      onDoneRef.current();
    };
    const onMove = (e: L.LeafletMouseEvent) => {
      if (!sv) return;
      if (sv.line) {
        sv.line.setLatLngs([sv.anchor, e.latlng]);
      } else {
        sv.line = L.polyline([sv.anchor, e.latlng], {
          color: "#3b82f6", weight: 2, dashArray: "6,4", interactive: false, pmIgnore: true,
        } as L.PolylineOptions).addTo(map);
      }
    };

    map.on("click", onClick);
    map.on("mousemove", onMove);
    return () => {
      map.off("click", onClick);
      map.off("mousemove", onMove);
      clear();
      el.style.cursor = "";
    };
  }, [map, active]);
}
